import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, TrendingUp, TrendingDown, Clock, ChevronDown, ChevronUp, Sun, Moon, Info, AlertTriangle, MapPin, Globe } from "lucide-react";
import NotificationManager from "@/components/NotificationManager";
import UpdateBanner from "@/components/UpdateBanner";
import { usePWATracking } from "@/hooks/usePWATracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Regions configuration with timezones
const REGIONS = [
  { id: 'peninsula', name: 'España peninsular', timezone: 'Europe/Madrid' },
  { id: 'canarias', name: 'Canarias', timezone: 'Atlantic/Canary' },
  { id: 'baleares', name: 'Baleares', timezone: 'Europe/Madrid' },
  { id: 'ceuta', name: 'Ceuta', timezone: 'Europe/Madrid' },
  { id: 'melilla', name: 'Melilla', timezone: 'Europe/Madrid' }
];

// Category styles helper
const getCategoryStyles = (category) => {
  const styles = {
    success: {
      bg: "bg-[hsl(var(--category-success-bg))]",
      text: "text-[hsl(var(--category-success-dark))]",
      border: "border-[hsl(var(--category-success))]",
      dot: "bg-[hsl(var(--category-success))]",
      solid: "bg-[hsl(var(--category-success-dark))]"
    },
    warning: {
      bg: "bg-[hsl(var(--category-warning-bg))]",
      text: "text-[hsl(var(--category-warning-dark))]",
      border: "border-[hsl(var(--category-warning))]",
      dot: "bg-[hsl(var(--category-warning))]",
      solid: "bg-[hsl(var(--category-warning-dark))]"
    },
    danger: {
      bg: "bg-[hsl(var(--category-danger-bg))]",
      text: "text-[hsl(var(--category-danger-dark))]",
      border: "border-[hsl(var(--category-danger))]",
      dot: "bg-[hsl(var(--category-danger))]",
      solid: "bg-[hsl(var(--category-danger-dark))]"
    }
  };
  return styles[category] || styles.warning;
};

// Format price for display
const formatPrice = (price) => {
  return (price / 1000).toFixed(4).replace(".", ",");
};

// Format hour range
const formatHourRange = (hour) => {
  const start = hour.toString().padStart(2, "0");
  const end = ((hour + 1) % 24).toString().padStart(2, "0");
  return `${start}:00 - ${end}:00`;
};

// Format current date in Spanish
const formatCurrentDate = () => {
  const now = new Date();
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  
  const dayName = days[now.getDay()];
  const day = now.getDate();
  const month = months[now.getMonth()];
  const year = now.getFullYear();
  
  return `${dayName}, ${day} de ${month} de ${year}`;
};

// Check if user is in a different timezone than the selected region
const isOutsideRegionTimezone = (regionId) => {
  try {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const region = REGIONS.find(r => r.id === regionId);
    const regionTimezone = region?.timezone || 'Europe/Madrid';
    
    // Check if user is in a Spanish timezone
    const spainTimezones = ['Europe/Madrid', 'Atlantic/Canary', 'Africa/Ceuta'];
    const isInSpain = spainTimezones.some(tz => userTimezone.includes(tz.split('/')[1]));
    
    if (!isInSpain) return true;
    
    // If user is in Spain but different region timezone
    if (regionId === 'canarias' && !userTimezone.includes('Canary')) {
      return false; // Don't show warning, just show the time
    }
    
    return false;
  } catch {
    return false;
  }
};

// Get time for a specific region
const getRegionTime = (regionId) => {
  try {
    const region = REGIONS.find(r => r.id === regionId);
    const timezone = region?.timezone || 'Europe/Madrid';
    return new Date().toLocaleTimeString('es-ES', { 
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return null;
  }
};

// Get current hour for a specific region
const getRegionHour = (regionId) => {
  try {
    const region = REGIONS.find(r => r.id === regionId);
    const timezone = region?.timezone || 'Europe/Madrid';
    const timeStr = new Date().toLocaleString('en-US', { timeZone: timezone });
    return new Date(timeStr).getHours();
  } catch {
    return new Date().getHours();
  }
};

// Theme Toggle Component
function ThemeToggle() {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem("theme");
      if (savedTheme) {
        document.documentElement.classList.toggle("dark", savedTheme === "dark");
        return savedTheme;
      }
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        document.documentElement.classList.add("dark");
        return "dark";
      }
    }
    return "light";
  });

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      data-testid="button-theme-toggle"
      className="rounded-full"
    >
      {theme === "light" ? (
        <Moon className="h-5 w-5" />
      ) : (
        <Sun className="h-5 w-5" />
      )}
    </Button>
  );
}

// Timezone Warning Banner - shows when user is outside Spain or viewing different region
function TimezoneWarning({ selectedRegion }) {
  const [show, setShow] = useState(false);
  const [regionTime, setRegionTime] = useState(getRegionTime(selectedRegion));
  
  useEffect(() => {
    // Show warning if user is outside any Spanish timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const spainTimezones = ['Europe/Madrid', 'Atlantic/Canary', 'Africa/Ceuta'];
    const isOutsideSpain = !spainTimezones.some(tz => userTimezone.includes(tz.split('/')[1]));
    setShow(isOutsideSpain);
    
    // Update time every minute
    setRegionTime(getRegionTime(selectedRegion));
    const interval = setInterval(() => {
      setRegionTime(getRegionTime(selectedRegion));
    }, 60000);
    
    return () => clearInterval(interval);
  }, [selectedRegion]);
  
  const regionName = REGIONS.find(r => r.id === selectedRegion)?.name || 'España peninsular';
  
  if (!show) return null;
  
  return (
    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 border-2 border-blue-400 rounded-xl text-center" data-testid="timezone-warning">
      <div className="flex items-center justify-center gap-2 text-blue-700 dark:text-blue-300">
        <Globe className="w-5 h-5" />
        <span className="text-sm font-medium">
          Los horarios corresponden a {regionName}
          {regionTime && <span className="font-bold ml-1">(Ahora: {regionTime})</span>}
        </span>
      </div>
    </div>
  );
}

// Header Component
function Header() {
  return (
    <header className="text-center mb-6 p-6 bg-primary rounded-2xl text-primary-foreground" data-testid="header">
      <div className="text-5xl mb-2">
        <Zap className="w-12 h-12 mx-auto fill-yellow-400 text-yellow-400" />
      </div>
      <h1 className="text-2xl font-bold mb-2" data-testid="app-title">
        Precio de la &quot;luz&quot;
      </h1>
      <p className="text-base opacity-90 capitalize">
        {formatCurrentDate()}
      </p>
    </header>
  );
}

// Region Selector Component
function RegionSelector({ selectedRegion, onRegionChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTimes, setCurrentTimes] = useState({});
  
  const selectedRegionData = REGIONS.find(r => r.id === selectedRegion);
  const selectedRegionName = selectedRegionData?.name || 'España peninsular';
  const selectedRegionTime = getRegionTime(selectedRegion);
  
  // Update times every minute
  useEffect(() => {
    const updateTimes = () => {
      const times = {};
      REGIONS.forEach(r => {
        times[r.id] = getRegionTime(r.id);
      });
      setCurrentTimes(times);
    };
    
    updateTimes();
    const interval = setInterval(updateTimes, 60000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="mb-4">
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between gap-2 p-3 bg-card border-2 border-border rounded-xl hover:border-primary transition-colors"
          data-testid="region-selector"
        >
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            <span className="font-medium text-foreground">
              Precios PVPC para {selectedRegionName}
            </span>
            <span className="text-sm text-muted-foreground">
              ({selectedRegionTime})
            </span>
          </div>
          <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-card border-2 border-border rounded-xl shadow-lg overflow-hidden">
            {REGIONS.map((region) => (
              <button
                key={region.id}
                onClick={() => {
                  onRegionChange(region.id);
                  setIsOpen(false);
                }}
                className={`w-full text-left p-3 hover:bg-muted transition-colors flex items-center justify-between ${
                  selectedRegion === region.id ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground'
                }`}
                data-testid={`region-option-${region.id}`}
              >
                <div className="flex items-center gap-2">
                  {selectedRegion === region.id && <span className="text-primary">✓</span>}
                  <span className={selectedRegion === region.id ? '' : 'ml-5'}>{region.name}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {currentTimes[region.id] || ''}
                  {region.id === 'canarias' && <span className="text-xs ml-1">(−1h)</span>}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {selectedRegion === 'peninsula' && (
        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1 mt-2">
          <AlertTriangle className="w-3 h-3 text-amber-500" />
          <span>Canarias, Baleares, Ceuta y Melilla pueden tener precios distintos</span>
        </p>
      )}
      
      {selectedRegion === 'canarias' && (
        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1 mt-2">
          <Clock className="w-3 h-3 text-blue-500" />
          <span>Canarias tiene 1 hora menos que la Península</span>
        </p>
      )}
    </div>
  );
}

// Legend Component
function Legend() {
  return (
    <Card className="flex justify-center gap-4 sm:gap-6 p-3 mb-6" data-testid="legend">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded-full bg-[hsl(var(--category-success))]" />
        <span className="text-xs sm:text-sm font-semibold text-foreground">MEJOR</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded-full bg-[hsl(var(--category-warning))]" />
        <span className="text-xs sm:text-sm font-semibold text-foreground">MEDIO</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded-full bg-[hsl(var(--category-danger))]" />
        <span className="text-xs sm:text-sm font-semibold text-foreground">ALTO</span>
      </div>
    </Card>
  );
}

// Current Price Highlight Card
function CurrentPriceCard({ prices, currentHour }) {
  if (!prices || prices.length === 0 || currentHour === null || currentHour === undefined) return null;
  
  const currentPrice = prices.find(p => p.hour === currentHour);
  if (!currentPrice) return null;
  
  const styles = getCategoryStyles(currentPrice.category);
  
  return (
    <div
      className={`p-6 rounded-2xl text-center mb-6 border-4 ${styles.bg} ${styles.border} shadow-md`}
      data-testid="current-price-card"
    >
      <p className="text-base font-bold text-foreground mb-2 uppercase">
        AHORA ({formatHourRange(currentHour)})
      </p>
      <p className={`text-4xl font-bold ${styles.text}`} data-testid="current-price-value">
        {formatPrice(currentPrice.price)} €/kWh
      </p>
    </div>
  );
}

// Summary Grid Component
function SummaryGrid({ minPrice, avgPrice, maxPrice }) {
  return (
    <section className="mb-6" data-testid="summary-grid">
      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
        <TrendingUp className="w-5 h-5" />
        Resumen del día
      </h2>
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 text-center bg-[hsl(var(--category-success-bg))] border-0">
          <p className="text-sm font-semibold text-foreground mb-1">Mínimo</p>
          <p className="text-xl sm:text-2xl font-bold text-[hsl(var(--category-success-dark))]" data-testid="min-price">
            {formatPrice(minPrice)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">€/kWh</p>
        </Card>
        <Card className="p-4 text-center bg-[hsl(var(--category-warning-bg))] border-0">
          <p className="text-sm font-semibold text-foreground mb-1">Media</p>
          <p className="text-xl sm:text-2xl font-bold text-[hsl(var(--category-warning-dark))]" data-testid="avg-price">
            {formatPrice(avgPrice)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">€/kWh</p>
        </Card>
        <Card className="p-4 text-center bg-[hsl(var(--category-danger-bg))] border-0">
          <p className="text-sm font-semibold text-foreground mb-1">Máximo</p>
          <p className="text-xl sm:text-2xl font-bold text-[hsl(var(--category-danger-dark))]" data-testid="max-price">
            {formatPrice(maxPrice)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">€/kWh</p>
        </Card>
      </div>
    </section>
  );
}

// Range Highlight
function RangeHighlight({ label, hours, variant }) {
  const bgClass = variant === "success" 
    ? "bg-[hsl(var(--category-success-dark))]" 
    : "bg-[hsl(var(--category-danger-dark))]";
  
  // Find consecutive ranges
  const formatRanges = (hours) => {
    if (!hours || hours.length === 0) return "";
    const sorted = [...hours].sort((a, b) => a - b);
    const ranges = [];
    let start = sorted[0];
    let end = sorted[0];
    
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === end + 1) {
        end = sorted[i];
      } else {
        ranges.push(`${start.toString().padStart(2, '0')}:00-${(end + 1).toString().padStart(2, '0')}:00`);
        start = sorted[i];
        end = sorted[i];
      }
    }
    ranges.push(`${start.toString().padStart(2, '0')}:00-${(end + 1).toString().padStart(2, '0')}:00`);
    return ranges.join(", ");
  };
  
  return (
    <div className={`${bgClass} text-white p-4 rounded-xl mb-4 flex flex-col items-center gap-1`}>
      <span className="text-base font-semibold opacity-90">{label}</span>
      <span className="text-lg font-bold text-center">{formatRanges(hours)}</span>
    </div>
  );
}

// Price Table - sorted by hour
function PriceTable({ data, currentHour, variant }) {
  if (!data || data.length === 0) return null;
  
  // Sort by hour
  const sortedData = [...data].sort((a, b) => a.hour - b.hour);
  
  return (
    <div className="overflow-hidden rounded-xl">
      <table className="w-full text-base">
        <tbody>
          {sortedData.map((item, index) => {
            const isCurrent = item.hour === currentHour;
            const prevItem = index > 0 ? sortedData[index - 1] : null;
            const isGap = prevItem && item.hour !== prevItem.hour + 1;
            const textColor = variant === "success" 
              ? "text-[hsl(var(--category-success-dark))]" 
              : "text-[hsl(var(--category-danger-dark))]";
            const bgColor = variant === "success" 
              ? "bg-[hsl(var(--category-success-bg))]" 
              : "bg-[hsl(var(--category-danger-bg))]";
            
            return (
              <tr
                key={item.hour}
                className={`${bgColor} ${isCurrent ? "ring-2 ring-primary ring-inset" : ""} ${isGap ? "border-t-[14px] border-t-background" : ""}`}
                data-testid={`price-row-${item.hour}`}
              >
                <td className={`p-3 font-bold text-lg ${textColor}`}>
                  {formatHourRange(item.hour)}
                  {isCurrent && <span className="text-primary font-bold text-sm ml-2">AHORA</span>}
                </td>
                <td className={`p-3 font-bold text-lg text-right ${textColor}`}>
                  {formatPrice(item.price)} €/kWh
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Recommendation Section
function RecommendationSection({ title, icon: Icon, rangeLabel, data, variant, currentHour }) {
  const titleColor = variant === "success" 
    ? "text-[hsl(var(--category-success-dark))]" 
    : "text-[hsl(var(--category-danger-dark))]";
  const hours = data.map(d => d.hour);
  
  return (
    <Card className="p-5 mb-4" data-testid={`section-${variant}`}>
      <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${titleColor}`}>
        <Icon className="w-5 h-5" />
        {title}
      </h2>
      <RangeHighlight label={rangeLabel} hours={hours} variant={variant} />
      <PriceTable data={data} currentHour={currentHour} variant={variant} />
    </Card>
  );
}

// All Hours Section
function AllHoursSection({ prices, currentHour }) {
  const [showAll, setShowAll] = useState(false);
  
  if (!prices || prices.length === 0) return null;
  
  // Sort by hour for display
  const sortedPrices = [...prices].sort((a, b) => a.hour - b.hour);
  
  return (
    <section className="mb-6" data-testid="all-hours-section">
      <Button
        onClick={() => setShowAll(!showAll)}
        className="w-full mb-4 text-lg font-bold"
        data-testid="button-toggle-all"
      >
        {showAll ? (
          <>
            <ChevronUp className="w-5 h-5 mr-2" />
            Ocultar todas las horas
          </>
        ) : (
          <>
            <ChevronDown className="w-5 h-5 mr-2" />
            Ver todas las horas
          </>
        )}
      </Button>
      
      {showAll && (
        <Card className="p-5 overflow-x-auto">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Todas las Horas
          </h2>
          <div className="overflow-hidden rounded-xl">
            <table className="w-full text-base">
              <tbody>
                {sortedPrices.map((item) => {
                  const styles = getCategoryStyles(item.category);
                  const isCurrent = item.hour === currentHour;
                  
                  return (
                    <tr
                      key={item.hour}
                      className={`${styles.bg} border-b-2 border-background ${isCurrent ? "ring-2 ring-primary ring-inset" : ""}`}
                      data-testid={`all-hours-row-${item.hour}`}
                    >
                      <td className={`p-3 font-bold text-lg ${styles.text} whitespace-nowrap`}>
                        {formatHourRange(item.hour)}
                        {isCurrent && <span className="text-primary font-bold text-sm ml-2">AHORA</span>}
                      </td>
                      <td className={`p-3 font-bold text-lg ${styles.text}`}>
                        {formatPrice(item.price)} €/kWh
                      </td>
                      <td className="p-3 text-center">
                        <Badge className={`${styles.dot} text-white font-bold`}>
                          {item.category === "success" && "MEJOR"}
                          {item.category === "warning" && "MEDIO"}
                          {item.category === "danger" && "ALTO"}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </section>
  );
}

// Tomorrow Warning Message
function TomorrowWarning() {
  return (
    <Card className="p-8 text-center bg-amber-50 dark:bg-amber-950 border-4 border-amber-500">
      <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
      <p className="text-xl font-bold text-amber-700 dark:text-amber-300 mb-2">
        Todavía no hay datos publicados para mañana
      </p>
      <p className="text-base text-amber-600 dark:text-amber-400">
        Generalmente estarán a partir de las 20:30h
      </p>
    </Card>
  );
}

// Day Content Component
function DayContent({ data, isToday = false, isTomorrow = false }) {
  if (isTomorrow && (!data || !data.available || !data.prices || data.prices.length === 0)) {
    return <TomorrowWarning />;
  }
  
  if (!data || !data.available || !data.prices || data.prices.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Zap className="w-12 h-12 mx-auto mb-4 text-primary animate-pulse" />
        <p className="text-lg text-muted-foreground">Cargando precios...</p>
      </Card>
    );
  }
  
  const { prices, minPrice, avgPrice, maxPrice, currentHour, dateLabel } = data;
  
  // Sort prices by price value for recommendations
  const sortedByPrice = [...prices].sort((a, b) => a.price - b.price);
  const cheapestHours = sortedByPrice.slice(0, 8);
  const expensiveHours = sortedByPrice.slice(-8);
  
  return (
    <div data-testid="day-content">
      <p className="text-center text-muted-foreground mb-4 capitalize" data-testid="date-label">
        {dateLabel}
      </p>
      
      {isToday && currentHour !== null && currentHour !== undefined && (
        <CurrentPriceCard prices={prices} currentHour={currentHour} />
      )}
      
      <SummaryGrid minPrice={minPrice} avgPrice={avgPrice} maxPrice={maxPrice} />
      
      <RecommendationSection
        title="MEJORES PRECIOS (recomendado)"
        icon={TrendingDown}
        rangeLabel="Mejores tramos:"
        data={cheapestHours}
        variant="success"
        currentHour={isToday ? currentHour : null}
      />
      
      <RecommendationSection
        title="PRECIOS ALTOS (evitar)"
        icon={TrendingUp}
        rangeLabel="Peores tramos:"
        data={expensiveHours}
        variant="danger"
        currentHour={isToday ? currentHour : null}
      />
      
      <AllHoursSection prices={prices} currentHour={isToday ? currentHour : null} />
    </div>
  );
}

// Info Section
function InfoSection() {
  return (
    <Card className="p-5 mb-4 bg-[hsl(var(--category-info-bg))] border-2 border-[hsl(var(--category-info))]">
      <h3 className="text-lg font-bold text-[hsl(var(--category-info-dark))] mb-3 flex items-center gap-2">
        <Info className="w-5 h-5" />
        Información
      </h3>
      <div className="text-[hsl(var(--category-info-dark))] text-sm space-y-2">
        <p>
          Los precios mostrados corresponden al <strong>PVPC</strong> (Precio Voluntario para el Pequeño Consumidor) 
          publicados por REE.
        </p>
        <p>
          Los precios de <strong>MAÑANA</strong> se publican alrededor de las 20:30h.
        </p>
      </div>
    </Card>
  );
}

// Footer Component
function Footer({ onShowLegal }) {
  return (
    <footer className="text-center p-4 border-t border-border mt-6">
      <p className="text-sm text-muted-foreground">Datos de REE (Red Eléctrica de España)</p>
      <p className="text-sm text-muted-foreground mb-2">3euros.app</p>
      <div className="flex justify-center gap-4 text-xs">
        <button 
          onClick={() => onShowLegal('aviso')}
          className="text-muted-foreground hover:text-primary underline"
        >
          Aviso Legal
        </button>
        <button 
          onClick={() => onShowLegal('privacidad')}
          className="text-muted-foreground hover:text-primary underline"
        >
          Política de Privacidad
        </button>
        <a 
          href="mailto:info@3euros.app"
          className="text-muted-foreground hover:text-primary underline"
        >
          Contacto
        </a>
      </div>
    </footer>
  );
}

// Legal Pages Component
function LegalPage({ type, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-background rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-foreground">
            {type === 'aviso' ? 'Aviso Legal' : 'Política de Privacidad'}
          </h2>
          <button 
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-2xl"
          >
            ×
          </button>
        </div>
        
        {type === 'aviso' && (
          <div className="text-sm text-foreground space-y-4">
            <section>
              <h3 className="font-bold mb-2">1. Información del titular</h3>
              <p>En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y Comercio Electrónico, se informa:</p>
              <ul className="list-disc ml-5 mt-2">
                <li><strong>Sitio web:</strong> 3euros.app</li>
                <li><strong>Actividad:</strong> Información sobre precios de electricidad PVPC</li>
                <li><strong>Contacto:</strong> info@3euros.app</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">2. Condiciones de uso</h3>
              <p>El acceso a este sitio web es gratuito. La información mostrada sobre precios de electricidad proviene de Red Eléctrica de España (REE) y se ofrece únicamente con fines informativos.</p>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">3. Propiedad intelectual</h3>
              <p>El diseño, código y contenidos originales de este sitio web están protegidos por derechos de propiedad intelectual. Los datos de precios son proporcionados por REE.</p>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">4. Exclusión de responsabilidad</h3>
              <p>La información de precios se obtiene de fuentes oficiales (REE), pero no garantizamos su exactitud absoluta. Recomendamos verificar los precios en la fuente oficial antes de tomar decisiones económicas.</p>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">5. Legislación aplicable</h3>
              <p>Este aviso legal se rige por la legislación española.</p>
            </section>
          </div>
        )}
        
        {type === 'privacidad' && (
          <div className="text-sm text-foreground space-y-4">
            <section>
              <h3 className="font-bold mb-2">1. Responsable del tratamiento</h3>
              <p>Contacto: info@3euros.app</p>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">2. Datos que recopilamos</h3>
              <p>Este sitio web <strong>NO recopila datos personales identificativos</strong>. No hay formularios de registro, no almacenamos emails ni nombres.</p>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">3. Cookies</h3>
              <p>Este sitio web <strong>NO utiliza cookies</strong> de seguimiento ni publicitarias.</p>
              <p className="mt-2">Solo utilizamos almacenamiento local del navegador (localStorage) para recordar:</p>
              <ul className="list-disc ml-5 mt-1">
                <li>Tu preferencia de tema (claro/oscuro)</li>
                <li>Tu región seleccionada</li>
                <li>Tu configuración de alertas (si las activas)</li>
              </ul>
              <p className="mt-2">Esta información permanece en tu dispositivo y nunca se envía a ningún servidor.</p>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">4. Notificaciones push (opcional)</h3>
              <p>Si activas las alertas de precios, almacenamos:</p>
              <ul className="list-disc ml-5 mt-1">
                <li><strong>Token de suscripción</strong>: Identificador técnico generado por tu navegador para enviarte notificaciones. No está vinculado a tu identidad.</li>
                <li><strong>Preferencias de alertas</strong>: Umbral de precio y tipo de alertas que deseas recibir.</li>
              </ul>
              <p className="mt-2"><strong>Base legal</strong>: Consentimiento explícito (art. 6.1.a RGPD). Al pulsar "Activar alertas" y aceptar el permiso del navegador, nos autorizas a enviarte notificaciones.</p>
              <p className="mt-2"><strong>Finalidad</strong>: Únicamente enviarte alertas sobre precios de electricidad según tu configuración.</p>
              <p className="mt-2"><strong>Cómo darte de baja</strong>: Pulsa el icono de campana y selecciona "Desactivar alertas". Tu token será eliminado inmediatamente.</p>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">5. Analítica web</h3>
              <p>Utilizamos <strong>Umami Analytics</strong>, una herramienta de analítica que:</p>
              <ul className="list-disc ml-5 mt-1">
                <li>NO utiliza cookies</li>
                <li>NO recopila datos personales</li>
                <li>NO rastrea usuarios individuales</li>
                <li>Solo cuenta visitas de forma anónima</li>
              </ul>
              <p className="mt-2">Cumple completamente con el RGPD sin necesidad de consentimiento.</p>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">6. Tus derechos</h3>
              <p>Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición y portabilidad contactando en info@3euros.app</p>
              <p className="mt-2">Para las notificaciones push, puedes desactivarlas en cualquier momento desde la propia aplicación.</p>
            </section>
            
            <section>
              <h3 className="font-bold mb-2">7. Cambios en esta política</h3>
              <p>Nos reservamos el derecho de modificar esta política. La fecha de última actualización es mayo de 2026.</p>
            </section>
          </div>
        )}
        
        <button 
          onClick={onClose}
          className="mt-6 w-full bg-primary text-primary-foreground py-2 rounded-lg font-semibold hover:opacity-90"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}

// Custom Tab Button Component
function TabButton({ label, isActive, isDisabled, onClick, testId }) {
  let className = "flex-1 py-3 px-4 text-base font-semibold rounded-lg transition-all ";
  
  if (isActive) {
    className += "bg-primary text-primary-foreground shadow-md";
  } else if (isDisabled) {
    className += "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-pointer";
  } else {
    className += "bg-muted text-foreground hover:bg-muted/80";
  }
  
  return (
    <button
      className={className}
      onClick={onClick}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

// Main App Component
function App() {
  // Track PWA installations
  usePWATracking();
  
  const [todayData, setTodayData] = useState(null);
  const [yesterdayData, setYesterdayData] = useState(null);
  const [tomorrowData, setTomorrowData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("today");
  const [error, setError] = useState(null);
  const [legalPage, setLegalPage] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem("selectedRegion") || "peninsula";
    }
    return "peninsula";
  });

  const tomorrowAvailable = tomorrowData && tomorrowData.available && tomorrowData.prices && tomorrowData.prices.length > 0;

  const fetchPrices = useCallback(async () => {
    try {
      setError(null);
      
      const [todayRes, yesterdayRes, tomorrowRes] = await Promise.allSettled([
        axios.get(`${API}/prices/today?region=${selectedRegion}`),
        axios.get(`${API}/prices/yesterday?region=${selectedRegion}`),
        axios.get(`${API}/prices/tomorrow?region=${selectedRegion}`)
      ]);
      
      if (todayRes.status === 'fulfilled') {
        setTodayData({
          ...todayRes.value.data,
          dateLabel: todayRes.value.data.date_label,
          minPrice: todayRes.value.data.min_price,
          maxPrice: todayRes.value.data.max_price,
          avgPrice: todayRes.value.data.avg_price,
          currentHour: todayRes.value.data.current_hour
        });
      }
      
      if (yesterdayRes.status === 'fulfilled') {
        setYesterdayData({
          ...yesterdayRes.value.data,
          dateLabel: yesterdayRes.value.data.date_label,
          minPrice: yesterdayRes.value.data.min_price,
          maxPrice: yesterdayRes.value.data.max_price,
          avgPrice: yesterdayRes.value.data.avg_price,
          currentHour: null
        });
      }
      
      if (tomorrowRes.status === 'fulfilled') {
        setTomorrowData({
          ...tomorrowRes.value.data,
          dateLabel: tomorrowRes.value.data.date_label,
          minPrice: tomorrowRes.value.data.min_price,
          maxPrice: tomorrowRes.value.data.max_price,
          avgPrice: tomorrowRes.value.data.avg_price,
          currentHour: null
        });
      }
      
    } catch (err) {
      console.error("Error fetching prices:", err);
      setError("Error al cargar los precios. Reintentando...");
    } finally {
      setLoading(false);
    }
  }, [selectedRegion]);

  useEffect(() => {
    fetchPrices();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchPrices, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchPrices]);

  // Update current hour every minute based on selected region
  useEffect(() => {
    const updateCurrentHour = () => {
      if (todayData) {
        // Get current hour in the selected region's timezone
        const regionHour = getRegionHour(selectedRegion);
        setTodayData(prev => prev ? { ...prev, currentHour: regionHour } : null);
      }
    };
    
    // Update immediately when region changes
    updateCurrentHour();
    
    const interval = setInterval(updateCurrentHour, 60 * 1000);
    return () => clearInterval(interval);
  }, [todayData, selectedRegion]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    localStorage.setItem("selectedRegion", region);
    setLoading(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Zap className="w-12 h-12 mx-auto mb-4 text-primary animate-pulse fill-yellow-400" />
          <p className="text-lg text-muted-foreground">Cargando precios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-xl mx-auto p-4">
        <div className="flex justify-end gap-1 mb-2">
          <NotificationManager selectedRegion={selectedRegion} />
          <ThemeToggle />
        </div>
        
        <Header />
        <TimezoneWarning selectedRegion={selectedRegion} />
        <RegionSelector selectedRegion={selectedRegion} onRegionChange={handleRegionChange} />
        <Legend />
        
        {error && (
          <Card className="p-4 mb-4 bg-destructive/10 border-destructive text-destructive text-center">
            {error}
          </Card>
        )}
        
        {/* Custom Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 p-1 bg-muted rounded-xl mb-4">
            <TabButton
              label="AYER"
              isActive={activeTab === "yesterday"}
              isDisabled={false}
              onClick={() => handleTabClick("yesterday")}
              testId="tab-yesterday"
            />
            <TabButton
              label="HOY"
              isActive={activeTab === "today"}
              isDisabled={false}
              onClick={() => handleTabClick("today")}
              testId="tab-today"
            />
            <TabButton
              label="MAÑANA"
              isActive={activeTab === "tomorrow"}
              isDisabled={!tomorrowAvailable}
              onClick={() => handleTabClick("tomorrow")}
              testId="tab-tomorrow"
            />
          </div>
          
          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === "yesterday" && (
              <DayContent data={yesterdayData} isToday={false} isTomorrow={false} />
            )}
            {activeTab === "today" && (
              <DayContent data={todayData} isToday={true} isTomorrow={false} />
            )}
            {activeTab === "tomorrow" && (
              <DayContent data={tomorrowData} isToday={false} isTomorrow={true} />
            )}
          </div>
        </div>
        
        <InfoSection />
        <Footer onShowLegal={setLegalPage} />
        
        {legalPage && (
          <LegalPage type={legalPage} onClose={() => setLegalPage(null)} />
        )}
        
        <UpdateBanner />
      </div>
    </div>
  );
}

export default App;
